import React, { useEffect, useRef, useState } from "react";
import analysisData from "../data/private/analysis.json";
import flowGuidanceData from "../data/private/flowGuidance.json";
import { logActivity, completeProgressStep } from "../utils/activity";
import normalizeApiError from "../utils/normalizeApiError";
import EmptyState from "../components/ui/EmptyState";
import ErrorState from "../components/ui/ErrorState";
import KeyValueTable from "../components/ui/KeyValueTable";
import LoadingState from "../components/ui/LoadingState";
import FlowGuidanceBanner from "../components/FlowGuidanceBanner";
import { getLatestAnalysis, runAnalysis } from "../api/analysis.api";
import { useNavigate } from "react-router-dom";
import { markSelectionUpdated, setDebugLastAction } from "../utils/debugState";
import {
  extractProjectId,
  getStoredProjectId,
  persistProjectId,
} from "../utils/projectId";

export default function DashboardAnalysis() {
  const navigate = useNavigate();
  const [analysisItems, setAnalysisItems] = useState([]);
  const [analysisClientId, setAnalysisClientId] = useState(null);
  const [analysisRaw, setAnalysisRaw] = useState(null);
  const [loading, setLoading] = useState(true);
  const [runLoading, setRunLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [runNotice, setRunNotice] = useState("");
  const requestedRef = useRef(new Set());

  const persistAnalysisId = (data) => {
    const analysisId =
      data?.id || data?.analysisId || data?.analysis_id || data?.meta?.id || "";
    if (analysisId) {
      localStorage.setItem("myv_selected_analysis_id", String(analysisId));
      markSelectionUpdated("analysis");
    }
  };

  const persistProjectIdFromPayload = (payload) => {
    const projectId = extractProjectId(payload);
    if (projectId) {
      persistProjectId(projectId);
    }
    return projectId;
  };

  const applyAnalysisResult = (data) => {
    setAnalysisItems(Array.isArray(data?.items) ? data.items : []);
    setAnalysisClientId(data?.clientId ?? null);
    setAnalysisRaw(data ?? null);
    setLoadError(null);
    persistAnalysisId(data);
    persistProjectIdFromPayload(data);
  };

  useEffect(() => {
    logActivity("analysis_viewed");
    completeProgressStep("analysis_viewed");
  }, []);

  const selectedClient = localStorage.getItem("myv_selected_client");
  const selectedProperty = localStorage.getItem("myv_selected_property");
  const selectedClientId =
    localStorage.getItem("myv_selected_client_id") || selectedClient || "";
  const selectedPropertyId =
    localStorage.getItem("myv_selected_property_id") || "";
  const selectedProjectId = getStoredProjectId();
  const selectedTemplate = localStorage.getItem("myv_selected_template");
  const guidance =
    !selectedClient
      ? {
          key: "analysis-missing-client",
          content: flowGuidanceData.analysis.missingClient,
          meta: { page: "analysis", missing: "client" },
        }
      : !selectedProperty
      ? {
          key: "analysis-missing-property",
          content: flowGuidanceData.analysis.missingProperty,
          meta: { page: "analysis", missing: "property" },
        }
      : !selectedTemplate
      ? {
          key: "analysis-missing-layout",
          content: flowGuidanceData.analysis.missingLayout,
          meta: { page: "analysis", missing: "layout" },
        }
      : null;

  useEffect(() => {
    const fetchAnalysis = async () => {
      if (!selectedProjectId) {
        setAnalysisItems([]);
        setAnalysisClientId(null);
        setAnalysisRaw(null);
        setLoading(false);
        return;
      }
      if (requestedRef.current.has(selectedProjectId)) {
        setLoading(false);
        return;
      }
      requestedRef.current.add(selectedProjectId);
      try {
        const data = await getLatestAnalysis(selectedProjectId || undefined);
        applyAnalysisResult(data);
        logActivity("analysis_loaded", { projectId: selectedProjectId || "" });
      } catch (error) {
        setAnalysisItems([]);
        setAnalysisClientId(null);
        setAnalysisRaw(null);
        setLoadError(normalizeApiError(error));
        logActivity("api_error", { module: "analysis" });
      } finally {
        setLoading(false);
      }
    };
    fetchAnalysis();
  }, [selectedProjectId]);

  const handleRunAnalysis = async () => {
    if (!selectedProjectId) {
      setLoadError(
        new Error(
          "Project ID is missing. Select a property with a project mapping to run analysis."
        )
      );
      return;
    }
    setRunLoading(true);
    try {
      const payload = {
        projectId: selectedProjectId || undefined,
        propertyId: selectedPropertyId || undefined,
      };
      const runResponse = await runAnalysis(payload);
      setRunNotice("Analysis request submitted.");
      setLoadError(null);
      logActivity("analysis_run", { projectId: selectedProjectId || "" });
      setDebugLastAction("Run analysis");
      const nextProjectId =
        (runResponse && persistProjectIdFromPayload(runResponse)) || selectedProjectId;
      if (runResponse) {
        persistAnalysisId(runResponse);
      }
      if (nextProjectId) {
        setLoading(true);
        try {
          const latest = await getLatestAnalysis(nextProjectId || undefined);
          applyAnalysisResult(latest);
          setDebugLastAction("Analysis results loaded");
        } catch (latestError) {
          setLoadError(normalizeApiError(latestError));
        } finally {
          setLoading(false);
        }
      }
    } catch (error) {
      const code = error?.response?.data?.code;
      if (code === "BILLING_CREDITS_EXHAUSTED") {
        navigate("/billing");
        setRunLoading(false);
        return;
      }
      logActivity("api_error", { module: "analysis" });
      setLoadError(normalizeApiError(error));
    } finally {
      setRunLoading(false);
    }
  };

  const missingClient = !selectedClientId;
  const missingProperty = !selectedPropertyId;
  const missingProjectId = !selectedProjectId;

  if (missingClient) {
    return (
      <div className="space-y-4">
        <EmptyState
          title="No client selected"
          message="Select a client to continue."
          testId="dashboard-empty-analysis-client"
        />
        <button className="btn btn-primary" onClick={() => navigate("/dashboard/clients")}>
          Select Client
        </button>
      </div>
    );
  }

  if (missingProperty) {
    return (
      <div className="space-y-4">
        <EmptyState
          title="No property selected"
          message="Select a property to continue."
          testId="dashboard-empty-analysis-property"
        />
        <button className="btn btn-primary" onClick={() => navigate("/dashboard/properties")}>
          Select Property
        </button>
      </div>
    );
  }

  if (missingProjectId) {
    return (
      <div className="space-y-4">
        <EmptyState
          title="Project mapping missing"
          message="Select a property that includes a project mapping to continue."
          testId="dashboard-empty-analysis-project"
        />
        <button className="btn btn-primary" onClick={() => navigate("/dashboard/properties")}>
          Select Property
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">
          {analysisData.title}
        </h1>
        <p className="mt-2 text-sm text-slate-600">{analysisData.subtitle}</p>
      </div>
      {guidance && (
        <FlowGuidanceBanner
          eventKey={guidance.key}
          title={guidance.content.title}
          description={guidance.content.description}
          meta={guidance.meta}
        />
      )}

      <div className="card card-hover p-6">
        <p className="text-sm text-slate-600">{analysisData.score.label}</p>
        <div className="mt-2 text-3xl font-semibold text-slate-900">—</div>
        <div className="mt-4 flex items-center gap-2 text-xs text-slate-600">
          <span>{analysisData.confidence.label}</span>
          <span className="rounded-full bg-slate-100 px-2 py-1 text-xs">
            {analysisData.confidence.level}
          </span>
          <span title={analysisData.confidence.tooltip} aria-label="Confidence info">
            ⓘ
          </span>
        </div>
        <button
          className="btn btn-primary mt-4"
          onClick={handleRunAnalysis}
          disabled={runLoading}
        >
          {runLoading
            ? analysisData.actions.runningLabel
            : analysisData.actions.runLabel}
        </button>
      </div>

      {!localStorage.getItem("myv_guidance_analysis") && (
        <div className="card p-4">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-sm font-semibold text-slate-900">
                {analysisData.guidance.title}
              </p>
              <p className="mt-1 text-sm text-slate-600">
                {analysisData.guidance.description}
              </p>
            </div>
            <button
              className="btn btn-secondary"
              onClick={() => {
                localStorage.setItem("myv_guidance_analysis", "true");
                logActivity("guidance_dismissed", { page: "analysis" });
              }}
            >
              {analysisData.guidance.ctaLabel}
            </button>
          </div>
        </div>
      )}

      {loading && <LoadingState variant="table" rows={4} />}
      {loadError && !loading && <ErrorState error={loadError} />}

      {!loading && analysisItems.length === 0 ? (
        <EmptyState
          title="No analyses yet"
          message="Run an analysis to see results here."
          testId="dashboard-empty-analysis"
        />
      ) : !loading ? (
        <div className="space-y-3">
          {analysisClientId && (
            <div className="card p-4 text-xs text-slate-600">
              Client ID: {analysisClientId}
            </div>
          )}
          {analysisItems.map((item, index) => (
            <div key={`analysis-item-${index}`}>
              {typeof item === "string" ? (
                <div className="card p-4 text-xs text-slate-600">{item}</div>
              ) : (
                <KeyValueTable data={item} />
              )}
            </div>
          ))}
        </div>
      ) : null}

      {analysisRaw && (
        <KeyValueTable data={analysisRaw} title="Latest analysis payload" />
      )}
      {runNotice && (
        <div className="card p-4 text-xs text-slate-600">{runNotice}</div>
      )}
    </div>
  );
}
